import i18next from "src/i18n/config";

import { Device, DeviceWarning, Group, License, PolicyStatus } from "@dashboard/devices/types";
import { Theme } from "@mui/material";

export type LicenseActivationStatusKey = "Used" | "Unused";

export const getLicenseActivationStatusKey = (license: License): LicenseActivationStatusKey => {
    return license?.device ? "Used" : "Unused";
};

export const getLicenseActivationStatusLabel = (status: LicenseActivationStatusKey) => {
    switch (status) {
        case "Used":
            return i18next.t("common:status.used");
        case "Unused":
        default:
            return i18next.t("common:status.unused");
    }
};

const emptyArrayHash = "d41d8cd98f00b204e9800998ecf8427e";

export function getDevicePolicyStatus(
    device: API.Device | Device | null,
    groupOrGroups: Group | Group[] | undefined,
): PolicyStatus | null {
    if (!device) {
        return null;
    }
    const group = Array.isArray(groupOrGroups)
        ? groupOrGroups.find((g) => g.licenseIds.includes(device.licenseId))
        : groupOrGroups;

    if (!group?.policyHash || group.policyHash === emptyArrayHash) {
        return PolicyStatus.NO_GROUP_POLICY;
    }

    if (device.lastState?.policy?.hash === group?.policyHash) {
        return device.lastState?.policy?.successful ? PolicyStatus.COMPLIANT : PolicyStatus.NON_COMPLIANT;
    }

    return PolicyStatus.NON_COMPLIANT;
}

type PolicyStatusKey = "Compliant" | "Non-compliant";

export const policyStatusKey = (status: PolicyStatus | null): PolicyStatusKey | null => {
    if (!status) {
        return null;
    }

    switch (status) {
        case PolicyStatus.COMPLIANT:
        case PolicyStatus.NO_GROUP_POLICY:
            return "Compliant";
        case PolicyStatus.NON_COMPLIANT:
            return "Non-compliant";
        default:
            return null;
    }
};

const policyStatusLabel = (key: PolicyStatusKey) => {
    switch (key) {
        case "Compliant":
            return i18next.t("pages:devicePage.options.compliant");
        case "Non-compliant":
            return i18next.t("pages:devicePage.options.nonCompliant");
        default:
            return i18next.t("pages:devicePage.options.compliant");
    }
};

export const policyStatusForTabs = (status: PolicyStatus | null): string | null => {
    if (status === PolicyStatus.NO_GROUP_POLICY) {
        return i18next.t("pages:devicePage.options.compliant") + " (" + i18next.t("pages:devicePage.options.noGroupPolicy") + ")";
    }
    const policyKey = policyStatusKey(status);
    if (!policyKey) {
        return null;
    }
    return policyStatusLabel(policyKey);
};

export const getPolicyIconColor = (status: PolicyStatus | null, theme: Theme): string => {
    if (status === PolicyStatus.COMPLIANT || status === PolicyStatus.NO_GROUP_POLICY) {
        return theme.palette.emteria.success;
    }
    if (status === PolicyStatus.NON_COMPLIANT) {
        return theme.palette.emteria.warning;
    }
    return theme.palette.emteria.text.secondary;
};

export const getSummaryIndicatorData = (
    license: License | null,
    deviceWarnings: DeviceWarning[],
    isExpired: boolean,
    theme: Theme,
    isValid?: boolean,
): {
    tooltip: string;
    color: string;
} => {
    const device = license?.device;

    if (isExpired) {
        return {
            tooltip: i18next.t("pages:devicePage.status.subscriptionExpired"),
            color: theme.palette.emteria.background.paperDeep,
        };
    } else if (!isValid) {
        return {
            tooltip: i18next.t("pages:devicePage.status.invalidSubscription"),
            color: theme.palette.emteria.warning,
        };
    } else if (!device) {
        return {
            tooltip: i18next.t("pages:devicePage.status.unusedLicense"),
            color: theme.palette.emteria.background.paperDeep,
        };
    } else if (!device?.isOnline) {
        return {
            tooltip: i18next.t("pages:devicePage.status.deviceOffline"),
            color: theme.palette.emteria.error,
        };
    } else if (deviceWarnings.includes("lowMemory")) {
        return {
            tooltip: i18next.t("pages:devicePage.status.lowMemory"),
            color: theme.palette.emteria.warning,
        };
    } else if (deviceWarnings.includes("lowBattery")) {
        return {
            tooltip: i18next.t("pages:devicePage.status.lowBattery"),
            color: theme.palette.emteria.warning,
        };
    } else if (deviceWarnings.includes("nonCompliant")) {
        return {
            tooltip: i18next.t("pages:devicePage.status.policyWarning"),
            color: theme.palette.emteria.warning,
        };
    } else {
        return {
            tooltip: i18next.t("pages:devicePage.status.deviceOnline"),
            color: theme.palette.emteria.success,
        };
    }
};
